<template>
  <th ref="element" class="group relative p-0">
    <button
      v-if="shown"
      type="button"
      :disabled="!header.column.getCanSort() || isSystemColumn(header.column)"
      :aria-disabled="!header.column.getCanSort()"
      :aria-label="systemColumnLabel"
      :class="[
        `flex size-full items-center gap-2 whitespace-nowrap rounded-md p-2 text-xs font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-card active:bg-card disabled:pointer-events-none disabled:opacity-100`,
        `outline-2 -outline-offset-2 outline-blue-500 focus-visible:outline`,
        first && `pl-4`,
        actualLast && `pr-4`,
        !last || isPrimaryColumn(header.column)
          ? `text-left`
          : `justify-end text-right`,
      ]"
      @click="header.column.getToggleSortingHandler()?.($event)"
    >
      <FlexRender
        :render="header.column.columnDef.header"
        :props="header.getContext()"
      />

      <ArrowUpIcon
        v-if="header.column.getIsSorted() === 'asc'"
        class="size-3"
      />
      <ArrowDownIcon
        v-else-if="header.column.getIsSorted() === 'desc'"
        class="size-3"
      />
    </button>

    <div
      v-if="dropIndicatorPosition"
      class="pointer-events-none fixed z-10 w-[3px] bg-blue-500"
      :style="{
        left: `${dropIndicatorPosition.x}px`,
        top: `${dropIndicatorPosition.top}px`,
        bottom: `calc(100vh - ${dropIndicatorPosition.bottom}px)`,
      }"
    />
  </th>
</template>

<script setup lang="ts" generic="Model extends GenericModel">
import type { Header } from "@tanstack/vue-table";
import { FlexRender } from "@tanstack/vue-table";
import { computed, useTemplateRef } from "vue";

import ArrowDownIcon from "@/icons/heroicons/arrow-down-micro.svg";
import ArrowUpIcon from "@/icons/heroicons/arrow-up-micro.svg";
import { useReorderableListItem } from "@/lib/pragmatic-dnd";
import {
  isFixedColumn,
  isPrimaryColumn,
  isSystemColumn,
} from "@/lib/tanstack-table";
import type { GenericModel } from "@/types/model";

const props = defineProps<{
  header: Header<Model, unknown>;
  reorderable: boolean;
  index: number;
  shown: boolean;
}>();

const element = useTemplateRef("element");
defineExpose({
  element: element,
});

const draggable = useReorderableListItem({
  element: element,
  index: computed(() => {
    if (!props.reorderable || isFixedColumn(props.header.column)) {
      return -1;
    }

    return props.index;
  }),
});

const systemColumnLabel = computed((): string | undefined => {
  const id = props.header.column.id;
  if (id === "$$selection") return "Select all";
  if (id === "$$actions") return "Actions";
  if (id === "$$toggle") return "Status";
  return undefined;
});

const first = computed(() => {
  return props.index === 0;
});

const actualLast = computed(() => {
  const header = props.header;
  const headers = header.headerGroup.headers;

  return props.index === headers.length - 1;
});

const last = computed(() => {
  const header = props.header;
  const headers = header.headerGroup.headers;

  const offset = headers.findLastIndex((header) => {
    return !isSystemColumn(header.column);
  });

  return props.index === offset;
});

const dropIndicatorPosition = computed(() => {
  if (draggable.edge === null || !element.value) {
    return null;
  }

  const rect = element.value.getBoundingClientRect();
  const scroller = element.value.closest("table")!.parentElement!;
  const scrollerRect = scroller.getBoundingClientRect();

  return {
    x: draggable.edge === "left" ? rect.left : rect.right,
    top: rect.top,
    bottom: scrollerRect.bottom,
  };
});
</script>
